import React, { useRef, useState, useCallback, useEffect } from 'react';
import { SplitPane as SplitPaneType } from '../../types';
import { PaneRenderer } from '../components/PaneRenderer';
import * as styles from '../components/SplitPane.css.ts';
import { vars } from '../theme/theme.css';

const MIN_PANE_SIZE = 250; // 最小ペインサイズ（px）
const MIN_PANE_HEIGHT = 200; // 最小ペイン高さ（px）

interface SplitPaneProps {
  node: SplitPaneType;
  onSplitRatioChange: (paneId: string, newRatio: number) => void;
  onTabDragStart?: (tabId: string, containerId: string, event: React.DragEvent) => void;
  onTabDragEnd?: () => void;
  onTabDrop?: (draggedTabId: string, targetContainerId: string, dropIndex?: number) => void;
}

/**
 * SplitPane (新バージョン)
 * 
 * Context APIと連携するシンプルなSplitPaneコンポーネント
 * - Props Drillingを排除
 * - 必要最小限のpropsのみ受け取る
 * - 子ペインの描画はPaneRendererに委譲
 */
export const SplitPane: React.FC<SplitPaneProps> = ({
  node,
  onSplitRatioChange,
  onTabDragStart,
  onTabDragEnd,
  onTabDrop
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const splitPaneRef = useRef<HTMLDivElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);

  const isHorizontal = node.direction === 'horizontal';

  // リサイザーのマウスダウンハンドラー
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  // ドラッグ中のマウス移動ハンドラー
  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (!isDragging || !splitPaneRef.current) return;

    const rect = splitPaneRef.current.getBoundingClientRect();
    let newRatio: number;

    if (isHorizontal) {
      const relativeX = event.clientX - rect.left;
      newRatio = relativeX / rect.width;
    } else {
      const relativeY = event.clientY - rect.top;
      newRatio = relativeY / rect.height;
    }

    // 最小サイズ制約を適用
    const containerSize = isHorizontal ? rect.width : rect.height;
    const minSize = isHorizontal ? MIN_PANE_SIZE : MIN_PANE_HEIGHT;
    const minRatio = minSize / containerSize;
    const maxRatio = 1 - minRatio;

    newRatio = Math.max(minRatio, Math.min(maxRatio, newRatio));
    onSplitRatioChange(node.id, newRatio);
  }, [isDragging, isHorizontal, node.id, onSplitRatioChange]);

  // マウスアップハンドラー
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // マウスイベントリスナーの設定とクリーンアップ
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isHorizontal ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp, isHorizontal]);

  // キーボードナビゲーションハンドラー
  const handleResizerKeyDown = (e: React.KeyboardEvent) => {
    const step = 0.05;
    let newRatio = node.splitRatio;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        newRatio = Math.max(0.1, node.splitRatio - step);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        newRatio = Math.min(0.9, node.splitRatio + step);
        break;
      case 'Home':
        newRatio = 0.1;
        break;
      case 'End':
        newRatio = 0.9;
        break;
      case 'Enter':
      case ' ':
        newRatio = 0.5;
        break;
      default:
        return;
    }

    e.preventDefault();
    onSplitRatioChange(node.id, newRatio);
  };

  return (
    <div
      ref={splitPaneRef}
      className={`${styles.splitPane} ${isHorizontal ? styles.horizontal : styles.vertical}`}
      data-testid={`split-pane-${node.id}`}
      style={{
        '--border-color': vars.color.border.primary,
        '--accent-color': vars.color.primary,
        '--focus-color': vars.color.border.focus,
      } as React.CSSProperties}
    >
      <div 
        className={styles.paneChild}
        style={{
          [isHorizontal ? 'width' : 'height']: `${node.splitRatio * 100}%`
        }}
      >
        <PaneRenderer
          node={node.leftChild}
          onTabDragStart={onTabDragStart}
          onTabDragEnd={onTabDragEnd}
          onTabDrop={onTabDrop}
        />
      </div>

      <div
        ref={resizerRef}
        className={`${styles.resizer} ${isHorizontal ? styles.horizontalResizer : styles.verticalResizer} ${isDragging ? styles.dragging : ''}`}
        onMouseDown={handleMouseDown}
        onKeyDown={handleResizerKeyDown}
        tabIndex={0}
        role="separator"
        aria-label={`Resize ${isHorizontal ? 'horizontal' : 'vertical'} split`}
        aria-orientation={isHorizontal ? 'horizontal' : 'vertical'}
        data-testid={`resizer-${node.id}`}
      />

      <div 
        className={styles.paneChild}
        style={{
          [isHorizontal ? 'width' : 'height']: `${(1 - node.splitRatio) * 100}%`
        }}
      >
        <PaneRenderer
          node={node.rightChild}
          onTabDragStart={onTabDragStart}
          onTabDragEnd={onTabDragEnd}
          onTabDrop={onTabDrop}
        />
      </div>
    </div>
  );
};

export default React.memo(SplitPane);